import { useEffect, useState, useMemo } from "react";
import { TranslationService } from "../../../services/TranslationService";
import { ToastService } from "../../shared/bootstrap/Toast";
import CompanyPaymentProviderService from "../../../services/CompanyPaymentProviderService";
import CompanyService, {
  DataType,
  Entities
} from "../../../services/CompanyService";
import {
  InvoiceFields,
  getInvoiceFieldTitle
} from "../../../services/InvoiceService";
import { ProviderListItem } from "../../../entities/company/PaymentProvider/ProviderListItem";
import { CompanyProviderConfig } from "../../../entities/company/PaymentProvider/CompanyProviderConfig";
import { ModalService, ModalParams } from "../../shared/bootstrap/modal/Modal";
import PaymentProviderModal from "./PaymentProviderModal";
import ProviderCurrencyModal from "./ProviderCurrencyModal";
import TooltipComponent from "../../shared/TooltipComponent";
import { MultiselectEditor } from "../../shared/components/Editors";
import Dropdown from "../../shared/components/Dropdown";
import Switch from "react-switch";

const PaymentGatewayConfiguration = () => {
  const { translate } = TranslationService;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [segmentIds, setSegmentIds] = useState<number[]>([]);
  const [defaultSegmentIds, setDefaultSegmentIds] = useState<number[]>([]);
  const [segmentOptionList, setSegmentOptionList] = useState<
    { value: string; label: string }[]
  >([]);

  // Estados de proveedores
  const [availableProviders, setAvailableProviders] = useState<
    ProviderListItem[]
  >([]);
  const [companyProviders, setCompanyProviders] = useState<
    CompanyProviderConfig[]
  >([]);

  // Estado para FinalStatusPaymentLink
  const [finalStatusPaymentLink, setFinalStatusPaymentLink] = useState<
    string | null
  >(null);
  const [finalStatusPaymentLinkLoading, setFinalStatusPaymentLinkLoading] =
    useState(false);

  // Estado para AllowsMultipleInvoices
  const [allowsMultipleInvoices, setAllowsMultipleInvoices] =
    useState<boolean>(true);
  const [allowsMultipleInvoicesLoading, setAllowsMultipleInvoicesLoading] =
    useState(false);

  // Estado para CustomerFieldAmountToPay
  const [customerFieldAmountToPay, setCustomerFieldAmountToPay] = useState<
    string | null
  >(null);
  const [customerFieldAmountToPayLoading, setCustomerFieldAmountToPayLoading] =
    useState(false);

  // Preparar el valor para MultiselectEditor (convertir IDs numéricos a string)
  const selectedSegmentsValue = useMemo(() => {
    return segmentIds
      .map((id) => {
        const segment = segmentOptionList.find(
          (s) => s.value === id.toString()
        );
        return segment ? { value: segment.value, label: segment.label } : null;
      })
      .filter(Boolean) as { value: string; label: string }[];
  }, [segmentIds, segmentOptionList]);

  // Callback para cuando cambian los segmentos seleccionados (convierte strings a números)
  const handleSegmentsChange = (values: string[]) => {
    setSegmentIds(values.map((v) => parseInt(v)));
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Cargar segmentos disponibles desde CompanyService.getSegments() (igual que en UserEdit.tsx)
        const segments = CompanyService.getSegments();
        const segmentOptions = segments
          .map((x) => ({ value: x.Id.toString(), label: x.Value }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setSegmentOptionList(segmentOptions);

        // Cargar configuración de segmentos desde getsegments para obtener los valores por defecto
        const segmentsConfigResponse =
          await CompanyPaymentProviderService.getSegmentsConfig();
        if (!(segmentsConfigResponse instanceof Error)) {
          // La respuesta puede venir con segmentIds directamente o con list que contiene segmentId
          let defaultIds: number[] = [];

          if (
            segmentsConfigResponse?.segmentIds &&
            Array.isArray(segmentsConfigResponse.segmentIds)
          ) {
            // Si viene segmentIds directamente
            defaultIds = segmentsConfigResponse.segmentIds;
          } else if (
            (segmentsConfigResponse as any)?.list &&
            Array.isArray((segmentsConfigResponse as any).list)
          ) {
            // Si viene con list, extraer los segmentId
            defaultIds = (segmentsConfigResponse as any).list
              .map((item: any) => item.segmentId)
              .filter((id: any) => id !== undefined && id !== null);
          }

          if (defaultIds.length > 0) {
            setDefaultSegmentIds(defaultIds);
            setSegmentIds(defaultIds);
          } else {
            setDefaultSegmentIds([]);
            setSegmentIds([]);
          }
        } else {
          // Si hay error, inicializar como array vacío
          setDefaultSegmentIds([]);
          setSegmentIds([]);
        }

        // Cargar proveedores disponibles
        const providersResponse =
          await CompanyPaymentProviderService.listPaymentProviders();
        if (!(providersResponse instanceof Error)) {
          setAvailableProviders(providersResponse.list);
        }

        // Cargar proveedores configurados para la compañía
        const companyProvidersResponse =
          await CompanyPaymentProviderService.getProvidersByCompany();
        if (!(companyProvidersResponse instanceof Error)) {
          setCompanyProviders(companyProvidersResponse.list);
        }

        // Cargar configuración de FinalStatusPaymentLink y AllowsMultipleInvoices
        const configsResponse = await CompanyService.getConfigsCompany();
        if (!(configsResponse instanceof Error)) {
          const configs = (configsResponse as any)?.configs;
          if (configs && Array.isArray(configs)) {
            const finalStatusConfig = configs.find(
              (config: any) => config.name === "finalstatuspaymentlink"
            );
            if (finalStatusConfig) {
              setFinalStatusPaymentLink(finalStatusConfig.value || null);
            }

            const allowsMultipleConfig = configs.find(
              (config: any) => config.name === "allowsmultipleinvoices"
            );
            if (allowsMultipleConfig) {
              // Convertir el valor string a boolean
              // El valor viene como "True" o "False" (string con primera letra mayúscula)
              const value = allowsMultipleConfig.value;
              // Convertir string "True"/"False" a boolean
              const boolValue =
                typeof value === "string"
                  ? value.toLowerCase() === "true"
                  : Boolean(value);
              setAllowsMultipleInvoices(boolValue);
            }

            const customerFieldAmountToPayConfig = configs.find(
              (config: any) => config.name === "customerfieldamounttopay"
            );
            if (
              customerFieldAmountToPayConfig &&
              customerFieldAmountToPayConfig.value
            ) {
              setCustomerFieldAmountToPay(customerFieldAmountToPayConfig.value);
            } else {
              // Si no hay configuración, establecer "Monto Pendiente" como valor por defecto
              setCustomerFieldAmountToPay(InvoiceFields.pending.toString());
            }
          }
        }
      } catch (e: any) {
        // En caso de error, inicializar segmentos como array vacío
        setSegmentIds([]);
        setDefaultSegmentIds([]);
        setSegmentOptionList([]);
        ToastService.showToast(
          e?.message ?? "Error al cargar la configuración.",
          undefined,
          "danger"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Guardar configuración de segmentos
      // Enviar los segmentos seleccionados (si no se modificó nada, segmentIds será igual a defaultSegmentIds)
      // Si segmentIds está vacío y hay valores por defecto, enviar los valores por defecto
      const segmentsToSave = segmentIds ?? defaultSegmentIds;

      const result =
        await CompanyPaymentProviderService.setSegmentsConfig(segmentsToSave);

      if (result instanceof Error) {
        ToastService.showToast(
          result.message ?? "Error al guardar la configuración.",
          undefined,
          "danger"
        );
        return;
      }

      // Actualizar los valores por defecto con los guardados
      setDefaultSegmentIds(segmentsToSave);

      ToastService.showToast(
        translate?.SavedSuccessfully ?? "Configuración guardada correctamente",
        undefined,
        "success"
      );
    } catch (e: any) {
      ToastService.showToast(
        e?.message ?? "Error al guardar la configuración.",
        undefined,
        "danger"
      );
    } finally {
      setSaving(false);
    }
  };

  // Preparar items para el dropdown de estados de factura
  const ioStatusItems = useMemo(() => {
    return CompanyService.getIoStatus().map((status) => ({
      value: status.IOStatusID.toString(),
      text: status.Value
    }));
  }, []);

  // Preparar items para el dropdown de campos de monto (Currency de Invoice)
  const currencyFieldItems = useMemo(() => {
    const additionalDefinitions = CompanyService.getAdditionalDefinitions();
    const additionalFields = additionalDefinitions
      .filter(
        (def) =>
          def.Entity === Entities.Invoice && def.Type === DataType.Currency
      )
      .map((def) => ({
        value: def.AdditionalDefinitionID.toString(),
        text: def.Name
      }))
      .sort((a, b) => a.text.localeCompare(b.text));

    // Agregar el campo fijo de monto pendiente al inicio (valor por defecto)
    const fixedPendingField = {
      value: InvoiceFields.pending.toString(),
      text:
        getInvoiceFieldTitle(InvoiceFields.pending) ||
        TranslationService.getTranslation("Pending") ||
        "Monto Pendiente"
    };

    // Agregar el campo fijo de monto
    const fixedAmountField = {
      value: InvoiceFields.amount.toString(),
      text:
        getInvoiceFieldTitle(InvoiceFields.amount) ||
        TranslationService.getTranslation("Amount") ||
        "Monto"
    };

    return [fixedPendingField, fixedAmountField, ...additionalFields];
  }, []);

  // Handler para cambiar el estado final del enlace de pago
  const handleFinalStatusPaymentLinkChange = async (
    valueUpdated: string | null
  ) => {
    setFinalStatusPaymentLinkLoading(true);
    try {
      const result = await CompanyService.setConfigCompany({
        finalstatuspaymentlink: valueUpdated || undefined
      });

      if (result instanceof Error) {
        ToastService.showToast(
          result.message ??
            "Error al guardar la configuración del estado final.",
          undefined,
          "danger"
        );
      } else {
        setFinalStatusPaymentLink(valueUpdated);
        ToastService.showToast(
          translate?.SavedSuccessfully ??
            "Configuración guardada correctamente",
          undefined,
          "success"
        );
      }
    } catch (e: any) {
      ToastService.showToast(
        e?.message ?? "Error al guardar la configuración del estado final.",
        undefined,
        "danger"
      );
    } finally {
      setFinalStatusPaymentLinkLoading(false);
    }
  };

  // Handler para cambiar allowsMultipleInvoices
  const handleAllowsMultipleInvoicesChange = async (checked: boolean) => {
    setAllowsMultipleInvoicesLoading(true);
    try {
      // Convertir boolean a string 'True' o 'False' como espera el backend
      const valueToSave = checked ? "True" : "False";
      const result = await CompanyService.setConfigCompany({
        allowsmultipleinvoices: valueToSave
      });

      if (result instanceof Error) {
        ToastService.showToast(
          result.message ??
            "Error al guardar la configuración de selección múltiple.",
          undefined,
          "danger"
        );
      } else {
        setAllowsMultipleInvoices(checked);
        ToastService.showToast(
          translate?.SavedSuccessfully ??
            "Configuración guardada correctamente",
          undefined,
          "success"
        );
      }
    } catch (e: any) {
      ToastService.showToast(
        e?.message ??
          "Error al guardar la configuración de selección múltiple.",
        undefined,
        "danger"
      );
    } finally {
      setAllowsMultipleInvoicesLoading(false);
    }
  };

  // Handler para cambiar customerFieldAmountToPay
  const handleCustomerFieldAmountToPayChange = async (
    valueUpdated: string | null | undefined
  ) => {
    setCustomerFieldAmountToPayLoading(true);
    try {
      // Si no se selecciona nada, usar "Monto Pendiente" como valor por defecto
      const valueToSave = valueUpdated || InvoiceFields.pending.toString();
      const result = await CompanyService.setConfigCompany({
        customerfieldamounttopay: valueToSave
      });

      if (result instanceof Error) {
        ToastService.showToast(
          result.message ??
            "Error al guardar la configuración del campo de monto a pagar.",
          undefined,
          "danger"
        );
      } else {
        // Actualizar el estado con el valor guardado (o "Monto Pendiente" si era null)
        setCustomerFieldAmountToPay(valueToSave);
        ToastService.showToast(
          translate?.SavedSuccessfully ??
            "Configuración guardada correctamente",
          undefined,
          "success"
        );
      }
    } catch (e: any) {
      ToastService.showToast(
        e?.message ??
          "Error al guardar la configuración del campo de monto a pagar.",
        undefined,
        "danger"
      );
    } finally {
      setCustomerFieldAmountToPayLoading(false);
    }
  };

  const reloadProviders = async () => {
    const companyProvidersResponse =
      await CompanyPaymentProviderService.getProvidersByCompany();
    if (!(companyProvidersResponse instanceof Error)) {
      setCompanyProviders(companyProvidersResponse.list);
    }
  };

  const handleAddProvider = () => {
    const modalParams = new ModalParams();
    modalParams.title = "Agregar Proveedor";
    modalParams.size = "modal-lg";
    modalParams.closeClickingOutside = false;
    modalParams.children = (
      <PaymentProviderModal
        availableProviders={availableProviders}
        editingProvider={null}
        onClose={() => ModalService.hideModal()}
        onSaved={reloadProviders}
      />
    );
    ModalService.showModal(modalParams);
  };

  const handleEditProvider = (provider: CompanyProviderConfig) => {
    const modalParams = new ModalParams();
    modalParams.title = "Editar Proveedor";
    modalParams.size = "modal-lg";
    modalParams.closeClickingOutside = false;
    modalParams.children = (
      <PaymentProviderModal
        availableProviders={availableProviders}
        editingProvider={provider}
        onClose={() => ModalService.hideModal()}
        onSaved={reloadProviders}
      />
    );
    ModalService.showModal(modalParams);
  };

  const handleDeleteProvider = (provider: CompanyProviderConfig) => {
    ModalService.showDefaultModal({
      title: translate.Delete ?? "Eliminar",
      message: `¿Está seguro que desea eliminar el proveedor de pago "${provider.PaymentProviderName}"?`,
      acceptButtonLabel: translate.Delete ?? "Eliminar",
      acceptButtonClassName: "btn btn-danger",
      onAcceptClick: () => confirmDeleteProvider(provider)
    });
  };

  const handleManageCurrencies = (provider: CompanyProviderConfig) => {
    const modalParams = new ModalParams();
    const currenciesLabel =
      TranslationService.getTranslation("Currencies") ?? "Monedas";
    modalParams.title = `${currenciesLabel} - ${provider.PaymentProviderName}`;
    modalParams.size = "modal-lg";
    modalParams.closeClickingOutside = false;
    modalParams.children = (
      <ProviderCurrencyModal
        provider={provider}
        onClose={() => ModalService.hideModal()}
        onSaved={reloadProviders}
      />
    );
    ModalService.showModal(modalParams);
  };

  const confirmDeleteProvider = async (provider: CompanyProviderConfig) => {
    try {
      setSaving(true);

      if (!provider.PaymentLinkProviderId) {
        ToastService.showToast(
          "ID del proveedor no válido",
          undefined,
          "danger"
        );
        return;
      }

      const result = await CompanyPaymentProviderService.deleteProvider(
        provider.PaymentLinkProviderId
      );

      if (result instanceof Error) {
        ToastService.showToast(
          result.message ?? "Error al eliminar el proveedor",
          undefined,
          "danger"
        );
      } else {
        ToastService.showToast(
          "Proveedor eliminado correctamente",
          undefined,
          "success"
        );
        await reloadProviders();
      }
    } catch (e: any) {
      ToastService.showToast(
        e?.message ?? "Error al eliminar el proveedor",
        undefined,
        "danger"
      );
    } finally {
      setSaving(false);
    }
  };

  // Verificar si se deben mostrar las secciones de desarrollo
  const showByRoleAndEnvironment = CompanyService.isBackOfficeUser();

  return (
    <div className="main-layout-container mx-3">
      <div className="main-panel">
        <h4 className="mb-4">Configuración de Pasarela de Pagos</h4>

        {loading ? (
          <div>
            <i className="fas fa-spinner-third fa-spin third ms-2"></i>
          </div>
        ) : (
          <>
            {/* Sección: Configuración General */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">Configuración General</h5>
              </div>
              <div className="card-body">
                {/* Segmentos de Clientes */}
                <div className="form-group mb-3">
                  <label className="form-label">Segmentos de clientes</label>
                  <MultiselectEditor
                    className="mt-2"
                    items={segmentOptionList}
                    callback={handleSegmentsChange}
                    placeholder={
                      translate?.SelectSegments ?? "Seleccionar segmentos"
                    }
                    value={selectedSegmentsValue}
                  />
                  <small className="form-text text-muted">
                    Selecciona los segmentos de clientes que podrán utilizar la
                    pasarela de pagos.
                  </small>
                </div>

                <div className="d-flex align-items-center gap-2 mb-3">
                  <button
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {translate?.Save ?? "Guardar"}
                  </button>
                  {saving && (
                    <i className="fas fa-spinner-third fa-spin third ms-2"></i>
                  )}
                </div>

                {/* Permite Selección Múltiple de Facturas */}
                {showByRoleAndEnvironment && (
                  <div className="form-group col-6 mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <label className="form-label mb-0">
                        {TranslationService.getTranslation(
                          "allowsMultipleInvoices"
                        ) ?? "Permitir Selección Múltiple de Facturas"}
                      </label>
                      {allowsMultipleInvoicesLoading ? (
                        <i className="fas fa-spinner-third fa-spin third ms-2"></i>
                      ) : (
                        <Switch
                          onColor="#080"
                          checked={allowsMultipleInvoices}
                          onChange={handleAllowsMultipleInvoicesChange}
                          height={24}
                          width={50}
                          uncheckedIcon={false}
                          disabled={allowsMultipleInvoicesLoading}
                        />
                      )}
                    </div>
                    <small className="form-text text-muted">
                      {allowsMultipleInvoices
                        ? (TranslationService.getTranslation(
                            "AllowsMultipleInvoicesDescription"
                          ) ??
                          "Los clientes podrán seleccionar múltiples facturas para pagar en una sola transacción.")
                        : (TranslationService.getTranslation(
                            "AllowsMultipleInvoicesDescriptionSingle"
                          ) ??
                          "Los clientes solo podrán seleccionar una factura a la vez para pagar.")}
                    </small>
                  </div>
                )}

                {/* Estado Final del Enlace de Pago */}
                {showByRoleAndEnvironment && (
                  <div className="form-group col-6 mb-3">
                    <label className="form-label">
                      {TranslationService.getTranslation(
                        "finalstatuspaymentlink"
                      ) ?? "Estado Final del Enlace de Pago"}
                    </label>
                    {finalStatusPaymentLinkLoading ? (
                      <div className="mt-2">
                        <i className="fas fa-spinner-third fa-spin third ms-2"></i>
                      </div>
                    ) : (
                      <div className="mt-2">
                        <Dropdown
                          items={ioStatusItems}
                          onChange={handleFinalStatusPaymentLinkChange}
                          value={finalStatusPaymentLink}
                          placeholder={
                            translate?.Select ?? "Seleccionar estado"
                          }
                        />
                      </div>
                    )}
                    <small className="form-text text-muted">
                      Selecciona el estado final que se aplicará a las facturas
                      cuando se complete el pago a través del enlace de pago.
                    </small>
                  </div>
                )}

                {/* Campo de Monto a Pagar */}
                {showByRoleAndEnvironment && (
                  <div className="form-group col-6 mb-3">
                    <label className="form-label">
                      {TranslationService.getTranslation(
                        "customerfieldamounttopay"
                      ) ?? "Campo de Monto a Pagar"}
                    </label>
                    {customerFieldAmountToPayLoading ? (
                      <div className="mt-2">
                        <i className="fas fa-spinner-third fa-spin third ms-2"></i>
                      </div>
                    ) : (
                      <div className="mt-2">
                        <Dropdown
                          items={currencyFieldItems}
                          onChange={handleCustomerFieldAmountToPayChange}
                          value={customerFieldAmountToPay}
                          optionLabel={translate?.Select ?? "Seleccione"}
                          placeholder={translate?.Select ?? "Seleccione"}
                        />
                      </div>
                    )}
                    <small className="form-text text-muted">
                      Selecciona el campo de tipo moneda que se utilizará para
                      calcular el totalizador al seleccionar facturas para pago.
                    </small>
                  </div>
                )}
              </div>
            </div>

            {/* Sección: Proveedores de Pago */}
            {showByRoleAndEnvironment && (
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Proveedores de Pago</h5>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={handleAddProvider}
                    disabled={saving}
                  >
                    <i className="fa fa-plus me-2"></i>
                    Agregar Proveedor
                  </button>
                </div>
                <div className="card-body">
                  {companyProviders.length === 0 ? (
                    <div className="text-muted text-center py-4">
                      No hay proveedores configurados. Haz clic en "Agregar
                      Proveedor" para comenzar.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Proveedor</th>
                            <th>Configuración</th>
                            <th className="text-end">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {companyProviders.map((provider) => (
                            <tr
                              key={provider.PaymentLinkProviderId}
                              className="show-child-on-hover"
                            >
                              <td>
                                <strong>{provider.PaymentProviderName}</strong>
                              </td>
                              <td>
                                <small className="text-muted">
                                  {provider.ValuesConfig.length} campo(s)
                                  configurado(s)
                                </small>
                              </td>
                              <td className="text-end">
                                <TooltipComponent
                                  title={translate.Edit ?? "Editar"}
                                  placement="right"
                                  className="align-middle"
                                >
                                  <i
                                    className="far fa-fw pointer fa-pen mx-2 show-when-hovering-parent"
                                    onClick={() =>
                                      !saving && handleEditProvider(provider)
                                    }
                                    style={{
                                      cursor: saving
                                        ? "not-allowed"
                                        : "pointer",
                                      opacity: saving ? 0.5 : 1
                                    }}
                                  />
                                </TooltipComponent>
                                {provider.PaymentLinkProviderId && (
                                  <>
                                    <TooltipComponent
                                      title={
                                        TranslationService.getTranslation(
                                          "ManageCurrencies"
                                        ) ?? "Gestionar Monedas"
                                      }
                                      placement="right"
                                      className="align-middle"
                                    >
                                      <i
                                        className="fas fa-fw pointer fa-coins mx-2 show-when-hovering-parent"
                                        onClick={() =>
                                          !saving &&
                                          handleManageCurrencies(provider)
                                        }
                                        style={{
                                          cursor: saving
                                            ? "not-allowed"
                                            : "pointer",
                                          opacity: saving ? 0.5 : 1
                                        }}
                                      />
                                    </TooltipComponent>
                                    <TooltipComponent
                                      title={translate.Delete ?? "Eliminar"}
                                      placement="right"
                                      className="align-middle"
                                    >
                                      <i
                                        className="far fa-fw pointer fa-trash text-danger mx-2 show-when-hovering-parent"
                                        onClick={() =>
                                          !saving &&
                                          handleDeleteProvider(provider)
                                        }
                                        style={{
                                          cursor: saving
                                            ? "not-allowed"
                                            : "pointer",
                                          opacity: saving ? 0.5 : 1
                                        }}
                                      />
                                    </TooltipComponent>
                                  </>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentGatewayConfiguration;
